import { DialogComponent } from '@theme/dialog';
import { getScrollContainer } from '@theme/scroll-container';

const KEY = 'lgr-welcome-newsletter-v1';
const DAY = 86400000;

function readExpiry(key) {
  try { return Number(localStorage.getItem(key)) || 0; } catch { return 0; }
}

function saveExpiry(key, time) {
  try { localStorage.setItem(key, String(time)); } catch {}
}

class WelcomeNewsletter extends DialogComponent {
  #timer;
  #retry;
  #abort;
  #shown = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const form = this.querySelector('form');
    if (!form) return;
    const pendingKey = KEY + '-pending';
    let recentSubmission = false;
    try { recentSubmission = Number(sessionStorage.getItem(pendingKey)) > Date.now() - 15 * 60000; } catch {}
    // Shopify challenge redirects can lose the anchor. Only a submission from this tab can confirm signup.
    document.addEventListener('submit', event => {
      if (!(event.target instanceof HTMLFormElement) || event.target.elements.form_type?.value !== 'customer') return;
      try {
        if (event.target === form) sessionStorage.setItem(pendingKey, String(Date.now()));
        else sessionStorage.removeItem(pendingKey);
      } catch {}
    }, { capture: true, signal });
    const hash = window.location.hash;
    const returnedHere = recentSubmission && (!hash || hash === '#' + form.id || hash === '#contact_form');
    const success = this.querySelector('[data-welcome-success]');
    const error = this.querySelector('[data-welcome-error]');

    this.addEventListener('dialog:open', this.#fitViewport, { signal });
    window.visualViewport?.addEventListener('resize', this.#fitViewport, { signal });
    window.visualViewport?.addEventListener('scroll', this.#fitViewport, { passive: true, signal });
    this.addEventListener('dialog:close', () => {
      if (this.dataset.result !== 'success' && !this.hasAttribute('data-design-mode')) {
        saveExpiry(KEY, Date.now() + Number(this.dataset.dismissDays || 7) * DAY);
      }
    }, { signal });

    if (returnedHere && (success || error)) {
      try { sessionStorage.removeItem(pendingKey); } catch {}
      if (success) {
        success.hidden = false;
        this.dataset.result = 'success';
        this.refs.dialog.setAttribute('aria-labelledby', success.querySelector('h2').id);
        saveExpiry(KEY + '-subscribed-until', Date.now() + 365 * DAY);
      } else {
        error.hidden = false;
        const email = form.querySelector('input[type="email"]');
        email.setAttribute('aria-invalid', 'true');
        email.setAttribute('aria-describedby', error.id);
        this.addEventListener('dialog:open', () => email.focus(), { once: true, signal });
      }
      this.#open();
    }

    if (this.hasAttribute('data-design-mode')) {
      this.addEventListener('shopify:section:select', () => this.#open(), { signal });
      return;
    }
    const openFromLink = () => {
      if (location.hash !== '#welcome10') return;
      if (!this.#subscribed()) this.#open();
      // A manually opened link should not reopen the popup on a later page refresh.
      history.replaceState(history.state, '', location.pathname + location.search);
    };
    window.addEventListener('hashchange', openFromLink, { signal });
    openFromLink();
    if (this.#shown || this.#subscribed() || readExpiry(KEY) > Date.now()) return;

    this.#timer = window.setTimeout(this.#tryAutomatic, Number(this.dataset.delay || 10) * 1000);
    document.addEventListener('scroll', event => {
      if (this.#shown || this.#retry) return;
      const page = getScrollContainer();
      if (event.target !== page && event.target !== document) return;
      const distance = page.scrollHeight - page.clientHeight;
      if (distance > 0 && page.scrollTop >= distance / 2) this.#tryAutomatic();
    }, { capture: true, passive: true, signal });
    document.addEventListener('mouseout', event => {
      if (event.relatedTarget === null && event.clientY <= 0 &&
          matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches) {
        this.#tryAutomatic();
      }
    }, { signal });
  }

  #subscribed() {
    // Previous releases stored an expiry 90 days after signup. Keep the original signup date.
    const legacy = readExpiry(KEY + '-subscribed');
    const until = Math.max(readExpiry(KEY + '-subscribed-until'), legacy ? legacy + (365 - 90) * DAY : 0);
    return this.dataset.result === 'success' || until > Date.now();
  }

  #fitViewport = () => {
    const dialog = this.refs.dialog;
    const viewport = window.visualViewport;
    if (!dialog.open || !viewport) return;
    // A soft keyboard can shrink or pan the visible area without resizing the layout viewport.
    // Leave pinch zoom to the browser so shoppers can magnify any part of the form.
    if (Math.abs(viewport.scale - 1) > 0.02) {
      dialog.style.removeProperty('--welcome-view-height');
      dialog.style.removeProperty('--welcome-view-top');
    } else {
      dialog.style.setProperty('--welcome-view-height', viewport.height + 'px');
      dialog.style.setProperty('--welcome-view-top', viewport.offsetTop + 'px');
      dialog.toggleAttribute('data-compact', viewport.height <= 650);
    }
  };

  #tryAutomatic = () => {
    if (!this.isConnected || this.#shown || this.#subscribed() || readExpiry(KEY) > Date.now()) return;
    const active = document.activeElement;
    if (document.hidden || active?.matches('input, textarea, select') || active?.isContentEditable ||
        document.querySelector('dialog[open], theme-drawer[open]')) {
      // The trigger has already fired. Retry after the shopper leaves the field or closes the drawer.
      if (!this.#retry) this.#retry = window.setTimeout(() => {
        this.#retry = undefined;
        this.#tryAutomatic();
      }, 750);
      return;
    }
    this.#open();
  };

  #open() {
    this.#shown = true;
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#retry);
    this.showDialog();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#retry);
    this.#retry = undefined;
    this.#abort?.abort();
    super.disconnectedCallback();
  }
}

if (!customElements.get('welcome-newsletter')) customElements.define('welcome-newsletter', WelcomeNewsletter);
